import React, { useState } from 'react'
import { styled } from '@mui/material/styles'
import { useDispatch, useSelector } from 'react-redux'
import { Checkbox } from '@mui/material'
import { renderInlineLoading } from '../../core/utils'

const PREFIX = 'ActionCheckbox'

const actionCheckboxClasses = {
  root: `${PREFIX}-root`,
  checked: `${PREFIX}-checked`,
}

const StyledCheckbox = styled(Checkbox)(({ theme }) => ({
  [`&.${actionCheckboxClasses.root}`]: {
    color: theme.palette.grey[600],
    padding: '9px',
    '&.Mui-checked': {
      color: theme.palette.primary.main,
    },
  },
}))

/**
 * @typedef {import('@mui/material').CheckboxProps} MuiCheckboxProps
 */

/**
 * @typedef {object} ActionCheckboxProps
 * @property {object} rowData - El objeto de datos completo para el elemento actual (generalmente una fila de una tabla).
 * @property {string} checkedField - La clave (key) dentro de `rowData` que contiene el valor booleano para determinar si el checkbox está marcado.
 * @property {any} actionType - Un identificador o tipo que se pasa a la función `action` o `onRequireConfirmation` para contextualizar la operación.
 * @property {function(object, any, function): object} action - El creador de acciones de Redux que se despachará al cambiar el estado. Recibe `rowData`, `actionType` y la función `setIsLoading`.
 * @property {string} [permissionKey='gestion_emails'] - La clave de permiso que se verificará en el estado global de Redux para habilitar o deshabilitar el checkbox.
 * @property {boolean} [requiresConfirmation=false] - Si es `true`, se llamará a `onRequireConfirmation` en lugar de despachar la acción directamente, permitiendo un paso de confirmación por parte del usuario.
 * @property {function(object, any, function): void} [onRequireConfirmation] - Función que se ejecuta cuando `requiresConfirmation` es `true`. Recibe `rowData`, `actionType` y `setIsLoading`. Ideal para abrir un modal de confirmación.
 * @property {MuiCheckboxProps} [checkboxProps] - Props adicionales que se pasarán directamente al componente `Checkbox` de Material-UI.
 */

/**
 * Un componente `Checkbox` especializado y conectado al estado de Redux, diseñado para ejecutar acciones asíncronas.
 * Gestiona su propio estado de carga, se habilita/deshabilita según los permisos del usuario extraídos del store de Redux,
 * y opcionalmente puede solicitar confirmación antes de ejecutar la acción principal.
 *
 * Es ideal para ser usado dentro de tablas o listas donde cada elemento tiene una acción booleana asociada
 * (ej. 'activar/desactivar', 'marcar como leído').
 *
 * @component
 * @example
 * // redux-actions.js
 * export const toggleUserActive = (userData, type, setIsLoading) => async (dispatch) => {
 * setIsLoading(true);
 * try {
 * await api.updateUser(userData.id, { isActive: !userData.isActive });
 * dispatch({ type: 'USER_UPDATED_SUCCESS', payload: userData.id });
 * } catch (error) {
 * console.error(error);
 * } finally {
 * setIsLoading(false);
 * }
 * };
 *
 * // Componente.js
 * const user = { id: 1, name: 'John Doe', isActive: false };
 *
 * const handleConfirmation = (rowData, actionType, setIsLoading) => {
 * if (window.confirm(`¿Seguro que quieres activar a ${rowData.name}?`)) {
 * // Si el usuario confirma, despachamos la acción.
 * dispatch(toggleUserActive(rowData, actionType, setIsLoading));
 * }
 * };
 *
 * <ActionCheckbox
 * rowData={user}
 * checkedField="isActive"
 * action={toggleUserActive}
 * permissionKey="gestion_usuarios"
 * />
 *
 * <ActionCheckbox
 * rowData={user}
 * checkedField="isActive"
 * action={toggleUserActive}
 * permissionKey="gestion_usuarios"
 * requiresConfirmation={true}
 * onRequireConfirmation={handleConfirmation}
 * />
 *
 * @param {ActionCheckboxProps} props - Las props del componente ActionCheckbox.
 */
export const ActionCheckbox = ({
  rowData,
  checkedField,
  actionType,
  action,
  permissionKey = 'gestion_emails',
  requiresConfirmation = false,
  onRequireConfirmation,
  ...checkboxProps
}) => {
  const dispatch = useDispatch()
  const permissions = useSelector((state) => state.global.permissions)
  const [isLoading, setIsLoading] = useState(false)

  const isChecked = rowData[checkedField] || false

  const handleToggle = (e) => {
    e.stopPropagation()
    if (isLoading) return

    if (requiresConfirmation && !isChecked && onRequireConfirmation) {
      onRequireConfirmation(rowData, actionType, setIsLoading)
    } else {
      dispatch(action(rowData, actionType, setIsLoading))
    }
  }

  return isLoading ? (
    renderInlineLoading()
  ) : (
    <div
      onClick={(e) => e.stopPropagation()}
      style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}
    >
      <StyledCheckbox
        className={actionCheckboxClasses.root}
        checked={isChecked}
        onChange={handleToggle}
        disabled={!permissions[permissionKey]}
        {...checkboxProps}
      />
    </div>
  )
}
